// ============================================================
// holdings - what a session holds of the GM's library, across kinds
// (plan assets-library, stage 2): its maps (by name), its scenes (by
// record id), its characters (by sheet id) and, since stage 3, its image
// and PDF handouts (by handout id), each with the origin its copy carries.
// The session view beside the table reads these; pure.
// ============================================================
import type { Character } from '../character/types';
import type { ParkedScene, SceneRecord } from '../scenes/types';
import type { Handout } from '../handouts/types';
import { ownedRow } from './provenance';
import type { AssetRow, Origin } from './types';

export type HeldKind = 'map' | 'scene' | 'character' | 'handout';

export interface Held {
  kind: HeldKind;
  /** What names it in the session: a map's name, a scene's record id, a sheet's id, a handout's id. */
  key: string;
  name: string;
  origin?: Origin | null;
}

export function heldOf(
  maps: readonly { name: string; origin?: Origin | null }[],
  scenes: readonly SceneRecord[],
  characters: readonly Character[],
  handouts: readonly Handout[] = [],
): Held[] {
  // A note or a link is made at the table; only a file can have come from the library.
  const files = handouts.filter((h) => h.kind === 'image' || h.kind === 'pdf');
  return [
    ...maps.map((m) => ({ kind: 'map' as const, key: m.name, name: m.name, origin: m.origin })),
    ...scenes.map((s) => ({ kind: 'scene' as const, key: s.id, name: s.name, origin: s.origin })),
    ...characters.map((c) => ({ kind: 'character' as const, key: c.id, name: c.name, origin: c.origin })),
    ...files.map((h) => ({ kind: 'handout' as const, key: h.id, name: h.title, origin: h.origin })),
  ];
}

/** The library's asset ids the session holds a copy of. */
export function copiedIds(held: readonly Held[], rows: readonly AssetRow[]): Set<string> {
  return new Set(held.flatMap((h) => (ownedRow(rows, h.origin) ? [h.origin!.assetId] : [])));
}

/** What the session holds of one asset, in words, for the details panel. */
export function heldNote(held: readonly Held[], asset: AssetRow): string {
  const copy = held.find((h) => h.origin?.assetId === asset.id);
  if (!copy?.origin) return 'Not in this session.';
  return copy.origin.version < asset.version
    ? `The session has v${copy.origin.version}; v${asset.version} is newer.`
    : `The session has v${copy.origin.version}, the latest.`;
}

/**
 * A scene's newer version written over its copy IN PLACE: the copy's id,
 * name, place in the rail and history stay; everything else is the
 * version's - its cast, marks, order and map - with the new origin.
 */
export function copyOver(record: SceneRecord, version: ParkedScene, origin: Origin): ParkedScene {
  const { id, name, order, createdAt, lastOnStageAt } = record;
  return { ...version, record: { ...version.record, id, name, order, createdAt, lastOnStageAt, origin } };
}
